import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { byKey } from "@/data/schoolsGlossary";
import { cn } from "@/lib/utils";

interface Props {
  glossaryKey: string;
  className?: string;
  invert?: boolean;
}

/** Small "i" popover attached to a KPI. Renders the definition + formula from the glossary. */
export default function KpiInfo({ glossaryKey, className, invert }: Props) {
  const entry = byKey[glossaryKey];
  if (!entry) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${entry.name}`}
          className={cn(
            "inline-grid place-items-center w-5 h-5 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            invert ? "bg-white/20 text-primary-foreground hover:bg-white/30" : "bg-muted text-muted-foreground hover:bg-muted/80",
            className
          )}
        >
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-80 text-sm">
        <div className="space-y-2">
          <div className="font-semibold leading-tight">{entry.name}</div>
          <p className="text-muted-foreground text-xs leading-relaxed">{entry.definition}</p>
          {entry.formula && (
            <pre className="text-[10px] bg-muted rounded-md p-2 overflow-x-auto font-mono">{entry.formula}</pre>
          )}
          {entry.scale && (
            <div className="text-[10px] text-muted-foreground">Scale: {entry.scale}</div>
          )}
          <Link
            to="/schools/glossary"
            className="text-xs text-primary hover:underline inline-block pt-1"
          >
            Open full glossary →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
